import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter } from 'rxjs';

import { NavbarComponent } from '../components/navbar/navbar.component';
import { ToastContainerComponent } from '../components/toast-container/toast-container.component';
import { BaseProjectDto } from 'app/api/backend-connector/models';
import { AuthenticationService } from 'app/services/authentication.service';
import { ProjectService } from 'app/services/project.service';

@Component({
  selector: 'app-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NavbarComponent, RouterOutlet, ToastContainerComponent],
  providers: [TranslateService, DatePipe],
})
export class RootComponent {
  readonly translate = inject(TranslateService);
  readonly authenticationService = inject(AuthenticationService);
  readonly router = inject(Router);
  readonly projectService = inject(ProjectService);
  readonly datePipe = inject(DatePipe);

  readonly loggedIn$ = new BehaviorSubject<boolean>(false);

  projectCode = '';
  projectStatus = '';
  lastUpdate = '';
  projectId = '';
  currentProject: BaseProjectDto | null = null;
  userName = 'John Doe';
  userRole = 'Admin';

  constructor() {
    void this.init();
    this.subscribeToRouteChanges();
  }

  private async init() {
    await this.authenticationService.init();
    setTimeout(() => {
      this.loggedIn$.next(true);
    }, 300);
  }

  private subscribeToRouteChanges(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateProjectInfo();
    });
  }

  private updateProjectInfo(): void {
    // Get project ID from route
    const urlSegments = this.router.url.split('/');
    const dashboardIndex = urlSegments.findIndex((segment) => segment === 'dashboard');

    if (dashboardIndex !== -1 && urlSegments[dashboardIndex + 1]) {
      const projectId = urlSegments[dashboardIndex + 1];
      void this.loadProjectInfo(projectId);
    } else {
      this.projectCode = '';
      this.projectStatus = '';
      this.lastUpdate = '';
      this.projectId = '';
      this.currentProject = null;
    }
  }

  private async loadProjectInfo(projectId: string): Promise<void> {
    try {
      const project = await this.projectService.getProject({ id: projectId });
      this.projectCode = project.code || '';
      this.projectStatus = project.status || '';
      this.lastUpdate = this.datePipe.transform(project.updatedAt, 'dd/MM/yy HH:mm') || '';
      this.projectId = projectId;
      this.currentProject = project;
    } catch (error) {
      console.error('Error loading project info:', error);
      this.projectCode = '';
      this.projectStatus = '';
      this.lastUpdate = '';
      this.projectId = '';
      this.currentProject = null;
    }
  }
}
